// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3C: simplified Hardware / Accessory popup.
//
// Horizontal label layout matching the Glass screenshot reference.
// Maps onto inventory_items + item_hardware_specs.
// ─────────────────────────────────────────────────────────────────

import { Checkbox, Col, Form, Input, InputNumber, Select } from 'antd';
import SimpleCreateModalShell from './SimpleCreateModalShell';
import TripleSalesRateInput from './TripleSalesRateInput';
import HardwareSetEditor, { deriveSetPayloadFromForm } from './HardwareSetEditor';
import { SIMPLE_KINDS, TAXABILITY_OPTIONS } from '../../simpleCreateConstants';
import { ItemCategorySelect, UomSelect, BrandSelect, GstRateSelect, StockItemSelect } from './simpleCreateFields';
import { HARDWARE_TYPES } from '../../studioConstants';

const GST_RULE = {
  validator: (_, v) =>
    v == null || v === '' || (Number(v) >= 0 && Number(v) <= 100)
      ? Promise.resolve()
      : Promise.reject(new Error('GST must be between 0 and 100'))
};
const PRICE_RULE = {
  validator: (_, v) =>
    v == null || v === '' || Number(v) >= 0
      ? Promise.resolve()
      : Promise.reject(new Error('Selling Price cannot be negative'))
};

export default function CreateHardwareModal({
  open, onClose, onSaved, editRecord = null, initialValues: initialValuesProp = null,
  viewOnly = false
}) {
  // Capture whether this item already had a set definition when the
  // popup opened — needed so the save path can DELETE the set when
  // the user toggles it OFF.
  const hadSet = Boolean(initialValuesProp && initialValuesProp._isSet);
  const initialValues = (editRecord || viewOnly) && initialValuesProp
    ? { ...initialValuesProp,
        _stockMode: initialValuesProp.sourceStockItemId ? 'derived' : 'self' }
    : { taxabilityType: 'taxable', salesRate: 0, _stockMode: 'self',
        _isSet: false, _setType: 'hardware_set', _setComponents: [] };
  return (
    <SimpleCreateModalShell
      kind={SIMPLE_KINDS.HARDWARE}
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      editRecord={editRecord}
      viewOnly={viewOnly}
      initialValues={initialValues}
      buildSetPayload={(form) => deriveSetPayloadFromForm(form, { wasSet: hadSet })}
      render={({ form }) => (
        <>
          {/* Row 1 — Product Name + Brand */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Product Name"
              name="itemName"
              required
              rules={[{ required: true, message: 'Product Name is required' }]}
            >
              <Input placeholder="e.g., Glass-to-Glass Hinge 90°" autoFocus />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Brand" name="brandId">
              <BrandSelect placeholder="Pick from Brand Master…" />
            </Form.Item>
          </Col>

          {/* Row 2 — Stock Name (75%) + Is Stock toggle (25%) */}
          <Form.Item name="sourceStockItemId" hidden><Input /></Form.Item>
          <Form.Item name="stockDesc"         hidden><Input /></Form.Item>
          <Form.Item name="_stockMode"        hidden><Input /></Form.Item>
          <Col xs={24} md={18}>
            <Form.Item label="Stock Name" tooltip="Optional — pick an existing base stock item or type a description.">
              <StockItemSelect
                mode={Form.useWatch('_stockMode', form) || 'self'}
                pickedId={Form.useWatch('sourceStockItemId', form)}
                textValue={Form.useWatch('stockDesc', form)}
                productName={Form.useWatch('itemName', form)}
                excludeId={editRecord && editRecord.id}
                onPick={(id) => form.setFieldValue('sourceStockItemId', id)}
                onChangeText={(text) => form.setFieldValue('stockDesc', text)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6} style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Checkbox
              checked={(Form.useWatch('_stockMode', form) || 'self') === 'self'}
              onChange={(e) => {
                const checked = e.target.checked;
                form.setFieldValue('_stockMode', checked ? 'self' : 'derived');
                if (checked) {
                  form.setFieldValue('sourceStockItemId', null);
                  form.setFieldValue('stockDesc', form.getFieldValue('itemName') || '');
                } else {
                  form.setFieldValue('stockDesc', '');
                }
              }}
            >
              Is Stock?
            </Checkbox>
          </Col>

          {/* Row 3 — Label Name (full width) */}
          <Col xs={24}>
            <Form.Item label="Label Name" name="labelDesc">
              <Input placeholder="Display label name" />
            </Form.Item>
          </Col>

          {/* Row 4 — Hardware Type + Model No */}
          <Col xs={24} md={12}>
            <Form.Item label="Hardware Type" name="hardwareType">
              <Select
                placeholder="Select or type new…"
                allowClear
                showSearch
                options={HARDWARE_TYPES.map((v) => ({ value: v, label: humanise(v) }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Model No" name="modelNo">
              <Input placeholder="e.g., HG-90" />
            </Form.Item>
          </Col>

          {/* Row 5 — Finish / Color + Item Category */}
          <Col xs={24} md={12}>
            <Form.Item label="Finish / Color" name="color">
              <Input placeholder="e.g., Brushed SS / Chrome / Black" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Item Category" name="itemCategoryId">
              <ItemCategorySelect placeholder="Select or type new…" />
            </Form.Item>
          </Col>

          {/* Row 6 — UOM + HSN/SAC */}
          <Col xs={24} md={12}>
            <Form.Item label="UOM" name="defaultUomId"
              rules={[{ required: true, message: 'UOM is required' }]}>
              <UomSelect placeholder="Select UOM (PCS, SET, KGS…)" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="HSN / SAC" name="hsncode">
              <Input maxLength={12} placeholder="e.g., 8302" />
            </Form.Item>
          </Col>

          {/* Row 7 — GST + Taxability */}
          <Form.Item name="gstRatePercent" hidden><Input /></Form.Item>
          <Col xs={24} md={12}>
            <Form.Item label="GST (%)" name="gstRateId"
              rules={[{ required: true, message: 'GST rate is required' }]}>
              <GstRateSelect form={form} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Taxability" name="taxabilityType">
              <Select options={TAXABILITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
            </Form.Item>
          </Col>

          {/* Row 8 — Selling Price (triple: Sell / Min / Max) */}
          <Form.Item name="minSalesRate" hidden><Input /></Form.Item>
          <Form.Item name="maxSalesRate" hidden><Input /></Form.Item>
          <Col xs={24}>
            <Form.Item
              label="Selling Price"
              name="salesRate"
              rules={[{ required: true, message: 'Selling Price is required' }, PRICE_RULE]}
            >
              <TripleSalesRateInput form={form} />
            </Form.Item>
          </Col>

          {/* Row 9 — Remarks (full width) */}
          <Col xs={24}>
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea rows={2} maxLength={500} placeholder="Add any additional notes here..." />
            </Form.Item>
          </Col>

          {/* Row 10 — Stage 6B.8A-3H: Hardware Set / Kit editor (full-bleed,
              no outer Form.Item label — the editor renders its own header). */}
          <Col xs={24} style={{ marginBottom: 16 }}>
            <HardwareSetEditor
              form={form}
              parentItemId={editRecord && editRecord.id}
              disabled={viewOnly}
            />
          </Col>
        </>
      )}
    />
  );
}

function humanise(s) {
  return String(s || '')
    .split('_')
    .map((w) => w ? (w[0].toUpperCase() + w.slice(1)) : '')
    .join(' ');
}
